import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button, Input, Wordmark } from '@novaquantis/design-system';
import { useValidateInvite, useAcceptInviteMutation } from '../queries/auth';
import { useAuth } from '../contexts/AuthContext';

const PAGE_CLASS =
  'flex flex-1 flex-col items-center justify-center gap-6 bg-background bg-[image:var(--nq-canvas-glow)] px-10 py-10';
const CARD_CLASS =
  'w-full max-w-[420px] rounded-2xl border border-[color:var(--border-slate)] bg-[color:var(--nq-surface-card-2)] px-11 py-9 shadow-[var(--shadow-card-soft),var(--inset-card-glow)]';
const HEADER_CLASS = 'mb-5 text-center';
const TITLE_CLASS =
  'mb-2 font-display text-2xl font-bold tracking-tight text-secondary [text-shadow:var(--glow-gold-text)]';
const SUBTITLE_CLASS = 'text-sm leading-normal text-[color:var(--text-secondary)]';
const ERROR_CLASS =
  'mb-4 rounded-md border border-negative/35 bg-negative/15 px-4 py-3 text-sm text-[color:var(--nq-down-soft)]';

const InviteAcceptPage = () => {
  const { hash } = useParams<{ hash: string }>();
  const navigate = useNavigate();
  const { setSession } = useAuth();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  const { data: inviteData, isPending: validating, error: validateError } = useValidateInvite(hash);
  const acceptMutation = useAcceptInviteMutation();

  const email = inviteData?.valid ? (inviteData.email ?? '') : '';
  const inviteError = validateError
    ? ((validateError as { response?: { data?: { error?: string } } }).response?.data?.error ?? 'Invalid or expired invite link')
    : inviteData && !inviteData.valid
      ? 'Invalid or expired invite link'
      : null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    if (!firstName.trim() || !lastName.trim()) {
      setFormError('First name and last name are required');
      return;
    }
    if (password !== confirmPassword) {
      setFormError('Passwords do not match');
      return;
    }
    if (password.length < 6) {
      setFormError('Password must be at least 6 characters');
      return;
    }
    if (username.length < 3) {
      setFormError('Username must be at least 3 characters');
      return;
    }

    try {
      const { data } = await acceptMutation.mutateAsync({
        hash: hash!,
        username,
        password,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
      });
      const { user, token: jwtToken, refreshToken } = data;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      setSession({ user: user as any, token: jwtToken, refreshToken });
      navigate('/onboarding', { replace: true });
    } catch (err: unknown) {
      setFormError(
        (err as { response?: { data?: { error?: string } } }).response?.data?.error ||
          'Failed to complete registration',
      );
    }
  };

  const brand = (
    <div className="flex flex-col items-center gap-3">
      <img
        src="/assets/logo-bull-trim.png"
        alt="NovaQuantis"
        className="h-[72px] w-auto drop-shadow-[0_8px_24px_rgba(37,180,224,0.3)]"
      />
      <Wordmark size={28} />
    </div>
  );

  if (validating) {
    return (
      <div className={PAGE_CLASS}>
        {brand}
        <div className={CARD_CLASS}>
          <div className={HEADER_CLASS}>
            <h1 className={TITLE_CLASS}>Validating invite</h1>
            <p className={SUBTITLE_CLASS}>Please wait…</p>
          </div>
        </div>
      </div>
    );
  }

  if (inviteError) {
    return (
      <div className={PAGE_CLASS}>
        {brand}
        <div className={CARD_CLASS}>
          <div className={HEADER_CLASS}>
            <h1 className={TITLE_CLASS}>Invalid invite</h1>
            <p className={ERROR_CLASS}>{inviteError}</p>
          </div>
          <Button variant="ghost" fullWidth onClick={() => navigate('/login')}>
            Go to login
          </Button>
        </div>
      </div>
    );
  }

  const loading = acceptMutation.isPending;

  return (
    <div className={PAGE_CLASS}>
      {brand}
      <div className={CARD_CLASS}>
        <div className={HEADER_CLASS}>
          <h1 className={TITLE_CLASS}>Complete your registration</h1>
          <p className={SUBTITLE_CLASS}>All fields are required. Your email is tied to this invite.</p>
        </div>

        <form onSubmit={handleSubmit} autoComplete="on">
          <Input
            label="Email (from invite)"
            name="email"
            type="email"
            value={email}
            readOnly
            tabIndex={-1}
            aria-readonly="true"
            title="This email is fixed to your invite and cannot be changed"
            autoComplete="email"
            variant="onDark"
          />

          <div className="grid grid-cols-1 gap-3 min-[480px]:grid-cols-2">
            <Input
              label="First name"
              name="firstName"
              value={firstName}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFirstName(e.target.value)}
              placeholder="First name"
              disabled={loading}
              autoComplete="given-name"
              required
              variant="onDark"
            />
            <Input
              label="Last name"
              name="lastName"
              value={lastName}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLastName(e.target.value)}
              placeholder="Last name"
              disabled={loading}
              autoComplete="family-name"
              required
              variant="onDark"
            />
          </div>

          <Input
            label="Username"
            name="username"
            value={username}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setUsername(e.target.value)}
            placeholder="Choose a username"
            minLength={3}
            disabled={loading}
            autoComplete="username"
            required
            variant="onDark"
          />

          <Input
            label="Password"
            name="new-password"
            type="password"
            value={password}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
            placeholder="At least 6 characters"
            minLength={6}
            disabled={loading}
            autoComplete="new-password"
            required
            variant="onDark"
          />

          <Input
            label="Confirm password"
            name="confirm-password"
            type="password"
            value={confirmPassword}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setConfirmPassword(e.target.value)}
            placeholder="Re-enter password"
            minLength={6}
            disabled={loading}
            autoComplete="new-password"
            required
            variant="onDark"
          />

          {formError && <div className={ERROR_CLASS}>{formError}</div>}

          <Button type="submit" disabled={loading} fullWidth variant="glow">
            {loading ? 'Creating account…' : 'Create account'}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default InviteAcceptPage;
